import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Image as ImageIcon, Sparkles, LayoutTemplate, Instagram, Smartphone, Megaphone, Video, Clapperboard, Wand2, Upload, Zap, ShieldCheck, MessageCircle, Landmark, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PublicLayout } from "@/components/app/PublicLayout";
import { PackageCard } from "@/components/app/Checkout";
import { api, num, tl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const els = ref.current?.querySelectorAll(".reveal") || [];
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("in")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
  return ref;
};

const TYPES = [
  { icon: ImageIcon, t: "AI Görsel", d: "Fikrinizi saniyeler içinde etkileyici görsellere dönüştürün.", k: "image" },
  { icon: Sparkles, t: "HD Görsel", d: "Baskıya hazır, ultra detaylı yüksek çözünürlük.", k: "hd_image" },
  { icon: LayoutTemplate, t: "Banner", d: "Web ve kampanyalar için geniş formatlı bannerlar.", k: "banner" },
  { icon: Instagram, t: "Instagram Post", d: "Akışta durduran kare gönderiler.", k: "instagram_post" },
  { icon: Smartphone, t: "Instagram Story", d: "Dikey, mobil öncelikli story tasarımları.", k: "instagram_story" },
  { icon: Megaphone, t: "Reklam Kreatifi", d: "Dönüşüm odaklı reklam görselleri.", k: "ad_creative" },
  { icon: Video, t: "Video", d: "Sinematik AI videolar, metinden veya görselden.", k: "video" },
  { icon: Clapperboard, t: "Reels", d: "Sosyal medya için dikey dinamik videolar.", k: "reels" },
];

const FAQ = [
  ["Kredi sistemi nasıl çalışır?", "Her içerik türünün bir kredi maliyeti vardır. Üretim başladığında kredi rezerve edilir; üretim başarılı olursa düşülür, başarısız olursa otomatik olarak iade edilir."],
  ["Prompt yazmak zorunlu mu?", "Hayır. Sadece açıklama yazabilir, sadece görsel yükleyebilir ya da ikisini birlikte kullanabilirsiniz."],
  ["Hangi ödeme yöntemleri var?", "Kredi/banka kartı ile PayTR güvencesiyle veya IBAN/Havale ile indirimli ödeme yapabilirsiniz."],
  ["Video üretirken sayfada beklemem gerekir mi?", "Hayır. Videolar arka planda üretilir; sayfadan ayrılsanız bile üretim devam eder ve galerinize eklenir."],
  ["WhatsApp bildirimleri nasıl çalışır?", "İzin verirseniz satın alma, üretim ve bakiye bildirimleri resmi WhatsApp Business API üzerinden gönderilir. İstediğiniz an kapatabilirsiniz."],
];

export default function Landing() {
  const ref = useReveal();
  const nav = useNavigate();
  const { user, settings } = useAuth();
  const [products, setProducts] = useState([]);
  const [pricing, setPricing] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);
  const reg = products.find((p) => p.kind === "REGISTRATION");
  const packs = products.filter((p) => p.kind === "CREDIT");
  const cost = (k) => pricing.find((r) => r.content_type === k)?.customer_credit_cost;
  const start = () => nav(user ? "/panel/olustur" : "/kayit");

  return (
    <PublicLayout>
      <div ref={ref}>
        <section className="relative overflow-hidden">
          <div className="grid-bg absolute inset-0 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
          <div className="absolute -top-40 left-1/4 h-[480px] w-[480px] rounded-full bg-indigo-600/25 blur-[120px]" />
          <div className="absolute top-20 right-0 h-[380px] w-[380px] rounded-full bg-fuchsia-600/20 blur-[120px]" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-16 sm:px-8 lg:grid-cols-12 lg:pt-24">
            <div className="relative z-10 lg:col-span-6 animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary">
                <Zap className="h-3.5 w-3.5" />Yapay zekâ destekli içerik stüdyosu
              </span>
              <h1 className="mt-6 text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="hero-title">
                Fikrinizi <span className="gradient-text">Profesyonel İçeriğe</span> Dönüştürün.
              </h1>
              <p className="mt-6 max-w-xl text-base text-muted-foreground sm:text-lg" data-testid="hero-subtitle">
                Yapay zekâ ile saniyeler içinde profesyonel görseller, bannerlar ve videolar oluşturun.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Button size="lg" className="btn-glow h-12 rounded-full px-7 text-sm font-bold tracking-wide" onClick={start} data-testid="hero-start-button">
                  HEMEN BAŞLA <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="h-12 rounded-full px-7 text-sm font-bold tracking-wide" asChild data-testid="hero-view-packages-button">
                  <a href="#paketler">PAKETLERİ GÖR</a>
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" />Güvenli ödeme</span>
                <span className="flex items-center gap-2"><Coins className="h-4 w-4 text-amber-500" />Başarısız üretimde kredi iadesi</span>
                <span className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-green-500" />WhatsApp bildirimleri</span>
              </div>
            </div>
            <div className="relative lg:col-span-6">
              <div className="relative mx-auto aspect-[4/5] max-w-md">
                <div className="glass-card absolute inset-0 overflow-hidden rounded-[2rem] p-2 animate-float-slow">
                  <img src="/img/ai_artwork_abstract.jpg" alt="AI ile üretilmiş sanat eseri örneği" className="h-full w-full rounded-[1.6rem] object-cover" />
                </div>
                <div className="glass-card absolute -left-6 top-10 w-36 overflow-hidden p-1.5 animate-float xl:-left-12">
                  <img src="/img/hero_ai_art.jpg" alt="AI tipografi örneği" className="aspect-square w-full rounded-xl object-cover" />
                  <div className="px-2 py-1.5 text-xs font-semibold">Instagram Post</div>
                </div>
                <div className="glass-card absolute -right-4 bottom-16 flex items-center gap-3 p-4 animate-float sm:-right-10" style={{ animationDelay: "1.5s" }}>
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500"><Wand2 className="h-5 w-5 text-white" /></span>
                  <div><div className="text-xs text-muted-foreground">Üretim tamamlandı</div><div className="font-display text-sm font-bold">Banner · 16:9</div></div>
                </div>
                <div className="glass-card absolute -bottom-6 left-6 flex items-center gap-2 px-4 py-3 text-sm animate-float" style={{ animationDelay: "3s" }}>
                  <Coins className="h-4 w-4 text-amber-500" /><span className="text-muted-foreground">Kalan kredi</span><b className="font-display">285</b>
                </div>
              </div>
            </div>
          </div>
          <div className="relative overflow-hidden border-y border-border bg-card/40 py-4">
            <div className="flex w-max animate-marquee gap-10 whitespace-nowrap text-sm font-semibold text-muted-foreground">
              {[...TYPES, ...TYPES, ...TYPES, ...TYPES].map((t, i) => <span key={i} className="flex items-center gap-2"><t.icon className="h-4 w-4 text-primary" />{t.t}</span>)}
            </div>
          </div>
        </section>

        <section id="ozellikler" className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
          <div className="reveal max-w-2xl">
            <div className="eyebrow">Tek platform, sınırsız format</div>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Ne oluşturmak istiyorsanız, birkaç saniye uzağınızda.</h2>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {TYPES.map((t, i) => (
              <div key={t.k} className="reveal glass-card glass-card-hover p-6" style={{ transitionDelay: `${i * 50}ms` }} data-testid={`feature-${t.k}`}>
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><t.icon className="h-5 w-5" /></span>
                <div className="mt-5 font-display text-lg font-semibold">{t.t}</div>
                <p className="mt-1.5 text-sm text-muted-foreground">{t.d}</p>
                {cost(t.k) && <div className="mt-4 text-xs font-semibold text-primary">{cost(t.k)} krediden başlayan</div>}
              </div>
            ))}
          </div>
        </section>

        <section id="nasil" className="border-y border-border bg-card/30">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-8 lg:grid-cols-3">
            <div className="reveal">
              <div className="eyebrow">Nasıl çalışır</div>
              <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Üç adımda profesyonel içerik.</h2>
              <p className="mt-4 text-muted-foreground">Tasarım bilgisi gerekmez. İçerik türünü seçin, isterseniz açıklama yazın veya görsel yükleyin, gerisini yapay zekâya bırakın.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3 lg:col-span-2">
              {[[Wand2, "Türü seçin", "Görsel, banner, post, story, reklam, video veya reels."], [Upload, "Prompt / görsel", "Açıklama yazın, görsel yükleyin ya da ikisini birden."], [Sparkles, "Üretin", "Sonuç galerinize eklenir, dilerseniz WhatsApp'tan haber alın."]].map(([I, t, d], i) => (
                <div key={t} className="reveal glass-card p-6" style={{ transitionDelay: `${i * 90}ms` }}>
                  <div className="font-display text-5xl font-extrabold text-primary/25">0{i + 1}</div>
                  <I className="mt-4 h-5 w-5 text-primary" />
                  <div className="mt-3 font-display font-semibold">{t}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="paketler" className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
          <div className="reveal flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-2xl">
              <div className="eyebrow">Paketler</div>
              <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Şeffaf fiyatlandırma, kullandığın kadar öde.</h2>
              <p className="mt-3 text-muted-foreground">Havale/EFT ile ödemelerde %{num(settings.iban_discount_rate || 0)} indirim uygulanır.</p>
            </div>
          </div>
          {reg && (
            <div className="reveal glass-card mt-12 grid items-center gap-6 overflow-hidden border-primary/40 p-8 md:grid-cols-[1.4fr_1fr]" data-testid="registration-package">
              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-indigo-500/10 via-violet-500/10 to-fuchsia-500/10" />
              <div>
                <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-primary">Başlangıç</span>
                <h3 className="mt-4 text-2xl font-bold">{reg.name}</h3>
                <p className="mt-2 text-muted-foreground">{reg.description}</p>
              </div>
              <div className="rounded-2xl border border-border bg-background/60 p-6">
                <div className="font-display text-4xl font-extrabold">{num(reg.credits)} <span className="text-base font-medium text-muted-foreground">kredi</span></div>
                <div className="mt-3 flex justify-between text-sm"><span className="text-muted-foreground">Kart</span><b>{tl(reg.card_price)}</b></div>
                <div className="mt-1 flex justify-between text-sm"><span className="text-muted-foreground">Havale/EFT</span><b className="text-emerald-500">{tl(reg.iban_price)}</b></div>
                <Button className="btn-glow mt-5 w-full rounded-xl" onClick={start} data-testid="registration-cta-button">Hemen Başla</Button>
              </div>
            </div>
          )}
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {packs.map((p) => <div className="reveal" key={p.id}><PackageCard p={p} onBuy={() => nav(user ? "/panel/satin-al" : "/kayit")} /></div>)}
          </div>
          <div className="reveal mt-10 grid gap-4 md:grid-cols-3">
            {[[ShieldCheck, "PayTR ile güvenli kart ödemesi"], [Landmark, "IBAN / Havale ile indirimli ödeme"], [MessageCircle, "WhatsApp ile anlık işlem bildirimi"]].map(([I, t]) => (
              <div key={t} className="flex items-center gap-3 rounded-xl border border-border px-5 py-4 text-sm"><I className="h-5 w-5 text-primary" />{t}</div>
            ))}
          </div>
        </section>

        <section id="sss" className="mx-auto max-w-3xl px-4 pb-24 sm:px-8">
          <div className="reveal text-center"><div className="eyebrow">SSS</div><h2 className="mt-3 text-3xl font-bold">Sıkça sorulan sorular</h2></div>
          <Accordion type="single" collapsible className="reveal mt-10">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={`q${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-base">{q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8">
          <div className="reveal relative overflow-hidden rounded-[2rem] border border-primary/30 p-10 sm:p-16">
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-indigo-600/30 via-violet-600/20 to-fuchsia-600/30" />
            <h2 className="max-w-2xl text-3xl font-bold sm:text-4xl">İlk profesyonel içeriğiniz birkaç tık uzağınızda.</h2>
            <Button size="lg" className="btn-glow mt-8 h-12 rounded-full px-8 font-bold" onClick={start} data-testid="bottom-cta-button">HEMEN BAŞLA <ArrowRight className="ml-2 h-4 w-4" /></Button>
            <Link to="/iletisim" className="ml-4 text-sm text-muted-foreground underline-offset-4 hover:underline">Bize ulaşın</Link>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
